/**
 * DRUM LIGHTS (2026-10-23) - the `instrument` lane's model for the editor and the compile. PURE: no signals, no
 * network, so the presets, the write-out and the preview can be tested on their own.
 *
 * Operator, 2026-10-23 (via OPBridge S336, OPApps/opbridge-opbuttons.md): "for the first 7 bars of the sequence the
 * snare is using lights X and Y with colours and then for the last 1 bar of the sequence all of the lights would
 * trigger from the snare, bright white. like a fill" and "i need it to be super simple to set up. the live play
 * obviously is good but it's not inuitive"; chose "drop a preset block", a cycle of 8 that "might need a 16 or 32 bar
 * loop". And to us: "pro grade that a novice could play around with and have fun".
 *
 * THE MODEL. A hit played live (OP-MidiFeed's `kick` / `snare` / ... on a Live track, or a note straight off a MIDI
 * port) lights the rig through the timeline that is running: a `hitRoute` step names the look a source lights from its
 * bar on. A DRUM LIGHTS BLOCK (`hitBlock`) is the novice's way in: one drum, one look for most of each cycle, a fill
 * look for its last bar(s), repeating; it is sent written out as routes and two looks (`unrollHitBlock`), so OPBridge
 * plays it with nothing new.
 *
 * HOW A HIT DRAWS is `hitMix.ts` (ours, ported by OPBridge). The preview here composes it the way OPBridge's
 * `live_lights::render_routed` does (read at their source 2026-10-23): a `pulse` is a hit whose attack is half its
 * length; `next` lights light `step % n`; a palette colour is `palette[step % len]`; a `strobe` flashes every 1/8 beat
 * at half duty. What it CANNOT show is the live part: which hits the drummer plays, and OPBridge's flash limit (3 new
 * flashes a second across the rig by default) holding some back.
 */
import {
  HIT_ROLES, isHitBlockStep, isHitRouteStep,
  type HitBlockLook, type HitBlockStep, type HitSource, type InstrumentColor, type InstrumentLook,
  type InstrumentLookCurve, type InstrumentLookStyle, type InstrumentLookVary, type TimelineLane, type TimelineStep,
} from '@opbuttons/schema';
import type { FixtureInstance } from '@opbuttons/schema';
import { hitColourAt, velocityLevel, type HitLook, type RoutedHit } from './hitMix.ts';
import type { Rgb01 } from './colorSample.ts';

// ---- words ----

/** What each drum is called on screen, in the order the picker offers them. */
export const DRUM_NAMES: Record<string, string> = {
  kick: 'Kick', snare: 'Snare', clap: 'Clap', hat: 'Hi-hat', openhat: 'Open hat', tom1: 'Tom 1', tom2: 'Tom 2', tom3: 'Tom 3',
  tom: 'Toms', crash1: 'Crash 1', crash2: 'Crash 2', cymbal: 'Cymbal', perc: 'Perc',
};
export const DRUMS: ReadonlyArray<{ role: string; name: string }> = HIT_ROLES.map((r) => ({ role: r, name: DRUM_NAMES[r] ?? r }));

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
export const noteName = (n: number): string => `${NOTE_NAMES[((n % 12) + 12) % 12]}${Math.floor(n / 12) - 1}`;

/** What a source listens for, in a few words ("Snare", "Any note on TD-17", "D1 on TD-17"). */
export function sourceLabel(s: HitSource): string {
  if (s.port) return s.note !== undefined ? `${noteName(s.note)} on ${s.port}` : `Any note on ${s.port}`;
  if (s.role) return DRUM_NAMES[s.role.toLowerCase()] ?? s.role;
  if (s.track) return `Track ${s.track.replace(/_/g, ' ')}`;
  if (s.note !== undefined) return `Note ${noteName(s.note)}`;
  return 'Nothing yet';
}

export const STYLE_NAMES: Record<InstrumentLookStyle, string> = { flash: 'Flash', pulse: 'Pulse', strobe: 'Strobe' };
export const VARY_NAMES: Record<InstrumentLookVary, string> = { none: 'All together', next: 'Chase', random: 'Random', alternate: 'Ping-pong' };
/** One line under each choice, so the words need no manual. */
export const VARY_HINTS: Record<InstrumentLookVary, string> = {
  none: 'Every hit lights every light.',
  next: 'Each hit lights the next light, round and round.',
  random: 'Each hit lights one light at random.',
  alternate: 'Hits take turns: the first half of the lights, then the second.',
};
export const STYLE_HINTS: Record<InstrumentLookStyle, string> = {
  flash: 'On at once, then falls back.',
  pulse: 'Swells up and back down.',
  strobe: 'Strobes for its length. Plays only when this show allows strobes.',
};

/** How long a hit lasts, in beats, as the panel offers it. */
export const FALL_CHOICES: ReadonlyArray<{ beats: number; name: string }> = [
  { beats: 0.25, name: 'Snap' }, { beats: 0.5, name: 'Short' }, { beats: 1, name: 'Long' }, { beats: 2, name: 'Glow' },
];
/** How often the block repeats, in bars. */
export const CYCLE_CHOICES: readonly number[] = [1, 2, 4, 8, 16, 32];
/** How much of each cycle's end is the fill, in bars (4/4: a quarter bar is the last beat). */
export const FILL_CHOICES: ReadonlyArray<{ bars: number; name: string }> = [
  { bars: 0.25, name: 'Last beat' }, { bars: 0.5, name: 'Last 2 beats' }, { bars: 1, name: 'Last bar' }, { bars: 2, name: 'Last 2 bars' },
];

export const WHITE: InstrumentColor = { r: 1, g: 1, b: 1 };
const RED: InstrumentColor = { r: 1, g: 0, b: 0 };
const BLUE: InstrumentColor = { r: 0, g: 0.2, b: 1 };

/** A colour for CSS (perceptual 0..1 -> rgb()). */
export function cssColour(c: { r: number; g: number; b: number } | null | undefined): string {
  if (!c) return 'rgb(0 0 0)';
  const k = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255);
  return `rgb(${k(c.r)} ${k(c.g)} ${k(c.b)})`;
}

// ---- the rig ----

/** A light a drum look can use: every light OPBridge patched (`/fixtures`, DMX), never a ceiling Hue spot (it does
 *  not list them, and OPBridge drops one from a look anyway). */
export function lookableLights(rig: readonly FixtureInstance[]): FixtureInstance[] {
  return rig.filter((f) => !f.id.startsWith('hue:'));
}

/** The light ids a block look lights on the PC whose rig is `rig`: every light there when `allLights`, else its own
 *  list as written. */
export function blockLookLights(look: HitBlockLook, rig: readonly FixtureInstance[]): string[] {
  return look.allLights ? lookableLights(rig).map((f) => f.id) : look.lights.filter((id) => !id.startsWith('hue:'));
}

/** The lights a preset puts a drum on, by what the light is (DEC-262's studio rig: back pars = kick, panels = snare,
 *  the bar = hats), else the first lights in patch order. Only a starting point: the panel changes it in a tap. */
function lightsForDrum(role: string, rig: readonly FixtureInstance[], n: number): string[] {
  const lights = lookableLights(rig);
  const is = (f: FixtureInstance, words: string[]) => words.some((w) => `${f.profileId} ${f.name}`.toLowerCase().includes(w));
  const want = role === 'snare' || role === 'clap' ? ['pl60', 'panel'] : role === 'kick' ? ['slimpar', ' par'] : role === 'hat' || role === 'openhat' ? ['colorband', 'bar'] : [];
  const picked = want.length ? lights.filter((f) => is(f, want)) : [];
  const list = picked.length ? picked : lights;
  return list.slice(0, Math.max(1, n)).map((f) => f.id);
}

// ---- making blocks: the presets ----

export function look(over: Partial<HitBlockLook> = {}): HitBlockLook {
  return { lights: [], colors: [{ ...WHITE }], style: 'flash', vary: 'none', fallBeats: 0.5, ...over };
}

/** The light tom `i` of three hits: spread across the rig, so a run down the toms walks the lights. */
function tomLight(rig: readonly FixtureInstance[], i: number): string[] {
  const lights = lookableLights(rig);
  const f = lights[Math.min(lights.length - 1, Math.floor((i * lights.length) / 3))];
  return f ? [f.id] : [];
}

export function makeBlock(id: string, position: number, durationBars: number, source: HitSource, main: HitBlockLook, fill?: HitBlockLook, cycleBars = 8): HitBlockStep {
  const cycle = Math.max(1, Math.min(cycleBars, durationBars >= 1 ? Math.floor(durationBars) : 1));
  return {
    id, kind: 'hitBlock', position, durationBars, cycleBars: cycle, source: { ...source }, look: main,
    ...(fill ? { fill: { bars: cycle >= 2 ? 1 : 0.25, look: fill } } : {}),
  };
}

export interface DrumPreset {
  id: string;
  name: string;
  /** One line under the name in the menu. */
  blurb: string;
  /** The lanes it adds, each with its block. */
  lanes: Array<{ name: string; block: (id: string, position: number, durationBars: number) => HitBlockStep }>;
}

/** The presets the "Drum lights" menu offers. `rig` is the light list they pick lights from. */
export function drumPresets(rig: readonly FixtureInstance[]): DrumPreset[] {
  const every = (o: Partial<HitBlockLook> = {}) => look({ allLights: true, ...o });
  // A fill always lands full white, whatever the velocity (OPBridge S336: the operator's test timeline's fill is 0).
  const fillWhite = () => every({ colors: [{ ...WHITE }], fallBeats: 0.5, velocitySensitivity: 0 });
  return [
    {
      id: 'snare-fill', name: 'Snare + fill', blurb: 'The snare flashes two lights; in the last bar of every 8, every light flashes white.',
      lanes: [{ name: 'Snare', block: (id, p, d) => makeBlock(id, p, d, { role: 'snare' },
        look({ lights: lightsForDrum('snare', rig, 2), colors: [{ ...RED }, { ...BLUE }] }), fillWhite()) }],
    },
    {
      id: 'kick-chase', name: 'Kick chase', blurb: 'Every kick lights the next light, round the rig.',
      lanes: [{ name: 'Kick', block: (id, p, d) => makeBlock(id, p, d, { role: 'kick' }, every({ colors: [{ ...RED }], vary: 'next' })) }],
    },
    {
      id: 'kick-snare-chase', name: 'Kick + snare chase', blurb: 'Kick and snare each move round the lights; the snare flashes every light white in the last bar of 8.',
      lanes: [
        { name: 'Kick', block: (id, p, d) => makeBlock(id, p, d, { role: 'kick' }, every({ colors: [{ ...RED }], vary: 'next' })) },
        { name: 'Snare', block: (id, p, d) => makeBlock(id, p, d, { role: 'snare' }, every({ colors: [{ ...BLUE }], vary: 'next' }), fillWhite()) },
      ],
    },
    {
      id: 'hat-sparkle', name: 'Hi-hat sparkle', blurb: 'Each hi-hat lights one light at random, quick and soft.',
      lanes: [{ name: 'Hi-hat', block: (id, p, d) => makeBlock(id, p, d, { role: 'hat' },
        look({ lights: lightsForDrum('hat', rig, 4), colors: [{ ...WHITE }], vary: 'random', fallBeats: 0.25, level: 0.3 })) }],
    },
    {
      id: 'full-kit', name: 'Full kit', blurb: 'A lane for every drum: kick, snare, both hats, three toms, two crashes. A tom run walks the lights; a crash flashes them all white.',
      lanes: [
        { name: 'Kick', block: (id, p, d) => makeBlock(id, p, d, { role: 'kick' }, look({ lights: lightsForDrum('kick', rig, 2), colors: [{ ...RED }] })) },
        { name: 'Snare', block: (id, p, d) => makeBlock(id, p, d, { role: 'snare' }, look({ lights: lightsForDrum('snare', rig, 2), colors: [{ ...RED }, { ...BLUE }], vary: 'next', fallBeats: 0.25 })) },
        { name: 'Hi-hat', block: (id, p, d) => makeBlock(id, p, d, { role: 'hat' }, look({ lights: lightsForDrum('hat', rig, 4), colors: [{ ...WHITE }], vary: 'random', fallBeats: 0.25, level: 0.3 })) },
        { name: 'Open hat', block: (id, p, d) => makeBlock(id, p, d, { role: 'openhat' }, look({ lights: lightsForDrum('openhat', rig, 4), colors: [{ ...WHITE }], fallBeats: 0.75, level: 0.3 })) },
        ...(['tom1', 'tom2', 'tom3'] as const).map((role, i) => ({ name: `Tom ${i + 1}`, block: (id: string, p: number, d: number) => makeBlock(id, p, d, { role }, look({ lights: tomLight(rig, i), colors: [{ ...BLUE }], fallBeats: 0.35 })) })),
        ...(['crash1', 'crash2'] as const).map((role, i) => ({ name: `Crash ${i + 1}`, block: (id: string, p: number, d: number) => makeBlock(id, p, d, { role }, every({ colors: [{ ...WHITE }], fallBeats: 1 })) })),
      ],
    },
  ];
}

// ---- what the lane plays ----

/** OPBridge's parser reads ONE look it cannot parse as "no looks at all" (`.ok()` over the whole list), so a look
 *  written by hand or by another app is sent with only the fields and values it accepts. */
const STYLES = new Set<InstrumentLookStyle>(['flash', 'pulse', 'strobe']);
const VARIES = new Set<InstrumentLookVary>(['none', 'next', 'random', 'alternate']);
const CURVES = new Set<InstrumentLookCurve>(['easeOut', 'linear', 'smooth', 'easeIn']);
const fin = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
function cleanColour(c: unknown): InstrumentColor | null {
  if (!c || typeof c !== 'object') return null;
  const o = c as Record<string, unknown>;
  if (!fin(o.r) || !fin(o.g) || !fin(o.b)) return null;
  return { r: o.r, g: o.g, b: o.b, ...(fin(o.w) ? { w: o.w } : {}), ...(fin(o.wTemp) ? { wTemp: o.wTemp } : {}) };
}
export function cleanWireLook(l: InstrumentLook): InstrumentLook {
  const color = cleanColour(l.color);
  const palette = Array.isArray(l.palette) ? l.palette.map(cleanColour).filter((c): c is InstrumentColor => !!c) : [];
  return {
    id: String(l.id),
    ...(typeof l.name === 'string' ? { name: l.name } : {}),
    ...(Array.isArray(l.lights) ? { lights: l.lights.filter((x): x is string => typeof x === 'string') } : {}),
    ...(typeof l.group === 'string' && l.group.trim() ? { group: l.group.trim() } : {}),
    ...(l.style && STYLES.has(l.style) ? { style: l.style } : {}),
    ...(color ? { color } : {}),
    ...(palette.length ? { palette } : {}),
    ...(fin(l.attackBeats) && l.attackBeats >= 0 ? { attackBeats: l.attackBeats } : {}),
    ...(fin(l.fallBeats) && l.fallBeats > 0 ? { fallBeats: l.fallBeats } : {}),
    ...(l.curve && CURVES.has(l.curve) ? { curve: l.curve } : {}),
    ...(fin(l.level) ? { level: Math.min(1, Math.max(0, l.level)) } : {}),
    ...(l.vary && VARIES.has(l.vary) ? { vary: l.vary } : {}),
    ...(fin(l.velocitySensitivity) ? { velocitySensitivity: Math.min(1, Math.max(0, l.velocitySensitivity)) } : {}),
  };
}

/** Whether a lane holds anything a drum plays (a block or a route). */
export const hasDrumSteps = (lane: TimelineLane): boolean => lane.steps.some((s) => isHitBlockStep(s) || isHitRouteStep(s));

/** Whether any look of the timeline strobes (the panel then offers the show's strobe opt-in). */
export function anyStrobeLook(lanes: readonly TimelineLane[], looks: readonly InstrumentLook[] | undefined): boolean {
  if ((looks ?? []).some((l) => l.style === 'strobe')) return true;
  return lanes.some((lane) => lane.steps.some((s) => isHitBlockStep(s) && (s.look.style === 'strobe' || (s.fill && !s.fill.off && s.fill.look.style === 'strobe'))));
}

// ---- the preview: what a hit looks like, without OPBridge ----

/** Which of `n` lights hit number `step` lights (OPBridge's `live_lights::pick`; `random` is seeded here so a preview
 *  repeats). */
export function pickLights(vary: InstrumentLookVary, n: number, step: number): number[] {
  if (n <= 0) return [];
  switch (vary) {
    case 'none': return Array.from({ length: n }, (_, i) => i);
    case 'next': return [step % n];
    case 'random': { const x = Math.sin((step + 1) * 12.9898) * 43758.5453; return [Math.floor((x - Math.floor(x)) * n) % n]; }
    case 'alternate': { const half = Math.ceil(n / 2); return step % 2 === 0 ? Array.from({ length: half }, (_, i) => i) : Array.from({ length: n - half }, (_, i) => half + i); }
  }
}

/** One preview hit: when (beats), its number for the look it lights (OPBridge counts per look) and how hard (1-127). */
export interface PreviewHit { atBeat: number; step: number; velocity?: number }

/** One look's hits in a preview: the look, the lights it lights (in order) and its hits. */
export interface PreviewLook { look: HitBlockLook; lights: readonly string[]; hits: readonly PreviewHit[] }

/** Every light's colour at `beat` (null = untouched; the panel draws that dark), from the hits of every look in
 *  `looks`, layered in start order across looks as OPBridge layers them, over black (a dark start; `hitMix` flag
 *  (d)). Velocity is `hitMix`'s own rule. A strobe is OPBridge's `Effect::Strobe` (on for half of every 1/8 beat),
 *  drawn over the rest while it is on. */
export function previewColours(looks: readonly PreviewLook[], beat: number): Map<string, Rgb01 | null> {
  const out = new Map<string, Rgb01 | null>();
  for (const pl of looks) for (const id of pl.lights) out.set(id, null);
  const per = new Map<string, RoutedHit[]>();
  const strobed = new Map<string, Rgb01>();
  for (const { look, lights, hits } of looks) {
    const colors = look.colors.length ? look.colors : [WHITE];
    const attack = Math.max(0, look.attackBeats ?? 0);
    const fall = Math.max(1 / 32, look.fallBeats);
    for (const h of hits) {
      if (h.atBeat > beat || beat - h.atBeat > attack + fall) continue;
      const c = colors[h.step % colors.length]!;
      const chosen = pickLights(look.vary, lights.length, h.step).map((k) => lights[k]!);
      if (look.style === 'strobe') {
        if (((beat - h.atBeat) % 0.125) >= 0.0625) continue;
        const lv = velocityLevel(look.level ?? 1, look.velocitySensitivity, h.velocity ?? 127);
        for (const id of chosen) strobed.set(id, { r: c.r * lv, g: c.g * lv, b: c.b * lv });
        continue;
      }
      const hl: HitLook = {
        color: c, level: look.level ?? 1, curve: look.curve ?? 'easeOut',
        ...(look.velocitySensitivity !== undefined ? { velocitySensitivity: look.velocitySensitivity } : {}),
        ...(look.style === 'pulse' ? { attackBeats: attack + fall / 2, fallBeats: fall / 2 } : { attackBeats: attack, fallBeats: fall }),
      };
      for (const id of chosen) {
        let arr = per.get(id);
        if (!arr) { arr = []; per.set(id, arr); }
        arr.push({ atBeat: h.atBeat, look: hl, velocity: h.velocity ?? 127 });
      }
    }
  }
  for (const [id, hs] of per) out.set(id, hitColourAt(null, hs, beat));
  for (const [id, c] of strobed) out.set(id, c);
  return out;
}

/** A practice groove for the preview, in beats from bar 0 of the cycle: where `role` hits in 4/4 and how hard (1-127),
 *  so a look that follows velocity shows its soft hits dimmer. The fill gets eighths that build up, as a drummer
 *  fills; everything else plays its usual spot with an accent or two. */
export function grooveFor(role: string | undefined, bars: number, fillFrom: number | null, beatsPerBar = 4): Array<{ beat: number; velocity: number }> {
  const out: Array<{ beat: number; velocity: number }> = [];
  const total = bars * beatsPerBar;
  const fillBeat = fillFrom === null ? null : fillFrom * beatsPerBar;
  const at = (beat: number, velocity: number) => { if (beat < total - 1e-9 && (fillBeat === null || beat < fillBeat - 1e-9)) out.push({ beat, velocity }); };
  for (let bar = 0; bar < bars; bar++) {
    const b0 = bar * beatsPerBar;
    if (role === 'kick') { at(b0, 120); at(b0 + 2, 105); at(b0 + 2.5, 70); }
    else if (role === 'snare' || role === 'clap') { at(b0 + 1, 110); at(b0 + 3, 118); }
    else if (role === 'crash1' || role === 'crash2') at(b0, 120);
    else if (role === 'hat' || role === 'openhat') { for (let i = 0; i < beatsPerBar * 2; i++) at(b0 + i / 2, i % 2 === 0 ? 100 : 60); }
    else for (let i = 0; i < beatsPerBar; i++) at(b0 + i, 100);
  }
  if (fillBeat !== null) {
    const n = Math.max(1, Math.round((total - fillBeat) * 2));
    for (let i = 0; i < n; i++) out.push({ beat: fillBeat + i / 2, velocity: Math.round(60 + (67 * i) / Math.max(1, n - 1)) });
  }
  return out.sort((a, b) => a.beat - b.beat);
}

/** The block a step is, or null. */
export const asBlock = (s: TimelineStep | undefined): HitBlockStep | null => (s && isHitBlockStep(s) ? s : null);
